import { useEffect, useCallback, useRef } from "react";

/**
 * Custom hook to warn users about unsaved changes when they try to leave the page
 * Shows a browser warning dialog ONLY when attempting to:
 * - Close the browser tab
 * - Close the browser window
 * - Click browser back/forward buttons
 * - Refresh the page (F5, Ctrl+R)
 * - Navigate to external URL
 *
 * Does NOT show warning on:
 * - Internal React Router navigation
 * - Programmatic navigation using navigate()
 *
 * @param shouldWarn - Boolean indicating if warning should be shown
 * @param message - Optional custom warning message (Note: Most modern browsers ignore custom messages)
 */
const useUnsavedChangesWarning = (
  shouldWarn: boolean,
  message = "Changes done in form may not be saved. Are you sure you want to leave?",
) => {
  const isNavigatingInternally = useRef(false);

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      // Only show warning if we should warn AND we're not navigating internally
      if (shouldWarn && !isNavigatingInternally.current) {
        // Cancel the event - this is required for the dialog to show
        event.preventDefault();

        // Chrome requires returnValue to be set
        event.returnValue = message;

        // Some browsers (legacy) might use the return value
        return message;
      }
      return undefined;
    },
    [shouldWarn, message],
  );

  useEffect(() => {
    // Only add the listener if we should show warnings
    if (shouldWarn) {
      window.addEventListener("beforeunload", handleBeforeUnload);
    }

    // Cleanup function to remove the event listener
    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      // Reset the flag when component unmounts
      isNavigatingInternally.current = false;
    };
  }, [shouldWarn, handleBeforeUnload]);

  // Return a function to allow programmatic navigation without warning
  return useCallback(() => {
    isNavigatingInternally.current = true;
    // Reset after navigation completes
    setTimeout(() => {
      isNavigatingInternally.current = false;
    }, 100);
  }, []);
};

export default useUnsavedChangesWarning;
